// eslint-disable-next-line
import { now, toCalendarDate, toCalendarDateTime } from '@internationalized/date';
import { isEmpty, isNil } from 'ramda';

import { ShiftSwapRequestSubtype } from '../../graphql/components';

import type { TValidator } from '../../utils/validations/types';
import type { ShiftSwapRequestFormValues } from './types';
import type { CalendarDate, CalendarDateTime, DateTimeDuration } from '@internationalized/date';

export const DATE_LESS_THAN_MIN_KEY = 'validation.invalidDateLessThanMin';
export const MAX_LENGTH_KEY = 'validation.maxLength';
export const MIN_DATE_RANGE_DIFFERENCE_KEY = 'validation.minDateRangeDifference';
export const REQUIRED_KEY = 'validation.required';
export const START_BEFORE_END_KEY = 'validation.startBeforeEnd';
export const IS_NOT_SWAPPABLE_KEY = 'validation.notSwappableShift';
export const REQUESTER_SHIFT_HAS_ALREADY_STARTED_KEY = 'validation.requestersShiftAlreadyInProgress';
export const RECIPIENT_SHIFT_HAS_ALREADY_STARTED_KEY = 'validation.recipientsShiftAlreadyInProgress';

export const required: TValidator = (value) => {
  if (isEmpty(value)) {
    return REQUIRED_KEY;
  }
  if (isNil(value)) {
    return REQUIRED_KEY;
  }
  // eslint-disable-next-line no-restricted-globals
  if (typeof value === 'number' && isNaN(value)) {
    return REQUIRED_KEY;
  }
  return undefined;
};

export const minDateTimeRange =
  (minRangeDuration: DateTimeDuration, skipValidationOptions?: { fieldName: string; value: boolean }): TValidator =>
  (dateInput: { start: CalendarDateTime; end: CalendarDateTime } | null, allValues) => {
    if (!dateInput) {
      return undefined;
    }

    if (skipValidationOptions && allValues[skipValidationOptions.fieldName] === skipValidationOptions.value) {
      return undefined;
    }

    const startDateIncrementedByMinRangeDuration = dateInput.start.add(minRangeDuration);

    // compare returns negative value if min range difference is not met
    if (dateInput.end.compare(startDateIncrementedByMinRangeDuration) < 0) {
      return MIN_DATE_RANGE_DIFFERENCE_KEY;
    }

    return undefined;
  };

export const minDate =
  (minDateRange: CalendarDate | CalendarDateTime): TValidator =>
  (dateInput: CalendarDate | CalendarDateTime | null) => {
    if (!dateInput) {
      return undefined;
    }

    // compare returns negative value if min range difference is not met
    if (dateInput.compare(minDateRange) < 0) {
      return DATE_LESS_THAN_MIN_KEY;
    }

    return undefined;
  };

export const startBeforeEnd =
  (includeTimeField?: { fieldName: string; value: boolean }): TValidator =>
  (dateInput: { start: CalendarDateTime; end: CalendarDateTime } | null, allValues) => {
    if (!dateInput) {
      return undefined;
    }

    const includeTime = includeTimeField && allValues[includeTimeField.fieldName] === includeTimeField.value;

    const startDate = includeTime ? dateInput.start : toCalendarDate(dateInput.start);
    const endDate = includeTime ? dateInput.end : toCalendarDate(dateInput.end);

    if (endDate.compare(startDate) < 0) {
      return START_BEFORE_END_KEY;
    }

    return undefined;
  };

export const maxChars =
  (chars: number): TValidator =>
  (value: string) =>
    value && value.length > chars ? MAX_LENGTH_KEY : undefined;

export const max255chars = maxChars(255);

export const isShiftSwappable: TValidator = (swappable) => {
  if (swappable) {
    return undefined;
  }

  return IS_NOT_SWAPPABLE_KEY;
};

export const isShiftAlreadyStarted =
  (requestSubtype: ShiftSwapRequestSubtype, timezone: string): TValidator =>
  (_swappable: boolean, allValues: ShiftSwapRequestFormValues) => {
    const startValue =
      requestSubtype === ShiftSwapRequestSubtype.Requester
        ? allValues?.requesterShift?.from
        : allValues?.recipientShift?.from;
    if (startValue && toCalendarDateTime(now(timezone)).compare(startValue) > 0) {
      return requestSubtype === ShiftSwapRequestSubtype.Requester
        ? REQUESTER_SHIFT_HAS_ALREADY_STARTED_KEY
        : RECIPIENT_SHIFT_HAS_ALREADY_STARTED_KEY;
    }

    return undefined;
  };
